/**
 * Hub-Kamera-Logik: Preview-Zoom auf Planet, Hub-Fokus, Zurück zur gespeicherten Position.
 * Reine Berechnung — keine Event-Subscription.
 */
import type { Camera } from '../renderer/Camera';
import type { CameraConfig } from '../config/CameraConfig';
import type { CameraTransition } from './CameraTransition';
import { axialToWorld } from '../math/HexMath';
import { pitchForMode } from './CameraPitch';
import { CameraMode } from './CameraMode';

export interface PlanetHubState {
  focusTarget: { x: number; z: number } | null;
  savedX: number;
  savedZ: number;
  savedZoom: number;
  inPreview: boolean;
}

export function createPlanetHubState(): PlanetHubState {
  return { focusTarget: null, savedX: 0, savedZ: 0, savedZoom: 0, inPreview: false };
}

export function previewPlanet(
  axialQ: number, axialR: number, hexRadius: number,
  cam: Camera, cfg: CameraConfig, trans: CameraTransition,
  state: PlanetHubState, currentZoom: number, viewOffsetX = 0,
): number {
  const [wx, wz] = axialToWorld(axialQ, axialR, hexRadius);
  if (!state.inPreview) {
    state.savedX = cam.target.x;
    state.savedZ = cam.target.z;
    state.savedZoom = currentZoom;
    state.inPreview = true;
  }
  state.focusTarget = { x: wx + viewOffsetX, z: wz };
  const zoomTarget = cfg.zoomMin;
  const sp = pitchForMode(CameraMode.Free, currentZoom, cfg);
  const ep = pitchForMode(CameraMode.Free, zoomTarget, cfg);
  trans.begin(cam, currentZoom, sp, ep);
  return zoomTarget;
}

export function cancelPreview(
  cam: Camera, cfg: CameraConfig, trans: CameraTransition,
  state: PlanetHubState, currentZoom: number,
): void {
  if (!state.inPreview) return;
  state.inPreview = false;
  state.focusTarget = null;
  const sp = pitchForMode(CameraMode.Free, currentZoom, cfg);
  const ep = pitchForMode(CameraMode.Free, state.savedZoom, cfg);
  trans.begin(cam, currentZoom, sp, ep);
}

export function focusPlanetHub(
  axialQ: number, axialR: number, hexRadius: number,
  cam: Camera, cfg: CameraConfig, trans: CameraTransition,
  state: PlanetHubState, currentZoom: number,
): number {
  const [wx, wz] = axialToWorld(axialQ, axialR, hexRadius);
  if (!state.inPreview) {
    state.savedX = cam.target.x;
    state.savedZ = cam.target.z;
    state.savedZoom = currentZoom;
  }
  state.inPreview = false;
  state.focusTarget = { x: wx, z: wz };
  const zoomTarget = cfg.hubZoom ?? cfg.zoomMin;
  const sp = pitchForMode(CameraMode.Free, currentZoom, cfg);
  const ep = pitchForMode(CameraMode.Free, zoomTarget, cfg);
  trans.begin(cam, currentZoom, sp, ep);
  return zoomTarget;
}

export function restoreFromHub(
  cam: Camera, cfg: CameraConfig, trans: CameraTransition,
  state: PlanetHubState, currentZoom: number,
): void {
  if (!state.focusTarget) return;
  state.focusTarget = null;
  state.inPreview = false;
  const sp = pitchForMode(CameraMode.Free, currentZoom, cfg);
  const ep = pitchForMode(CameraMode.Free, state.savedZoom, cfg);
  trans.begin(cam, currentZoom, sp, ep);
}
